function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _toConsumableArray(r) { return _arrayWithoutHoles(r) || _iterableToArray(r) || _unsupportedIterableToArray(r) || _nonIterableSpread(); }
function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _iterableToArray(r) { if ("undefined" != typeof Symbol && null != r[Symbol.iterator] || null != r["@@iterator"]) return Array.from(r); }
function _arrayWithoutHoles(r) { if (Array.isArray(r)) return _arrayLikeToArray(r); }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
/**
 * @module ol/expr/cpu
 */

import { ColorType, LiteralExpression, Ops, overlapsType, parse, typeName } from './expression.js';
import { fromString, lchaToRgba, normalize, rgbaToLcha, toString, withAlpha } from '../color.js';

/**
 * @fileoverview This module includes functions to build expressions for evaluation on the CPU.
 * Building is composed of two steps: parsing and compiling.  The parsing step takes an encoded
 * expression and returns an instance of one of the expression classes.  The compiling step takes
 * the expression instance and returns a function that can be evaluated in to return a literal
 * value.  The evaluator function should do as little allocation and work as possible.
 */

/**
 * @typedef {Object} EvaluationContext
 * @property {Object} properties The values for properties used in 'get' expressions.
 * @property {Object} variables The values for variables used in 'var' expressions.
 * @property {number} resolution The map resolution.
 * @property {string|number|null} featureId The feature id.
 * @property {string} geometryType Geometry type of the current object.
 */

/**
 * @return {EvaluationContext} A new evaluation context.
 */
export function newEvaluationContext() {
  return {
    variables: {},
    properties: {},
    resolution: NaN,
    featureId: null,
    geometryType: ''
  };
}

/**
 * @typedef {function(EvaluationContext):import("./expression.js").LiteralValue} ExpressionEvaluator
 */

/**
 * @typedef {function(EvaluationContext):boolean} BooleanEvaluator
 */

/**
 * @typedef {function(EvaluationContext):number} NumberEvaluator
 */

/**
 * @typedef {function(EvaluationContext):string} StringEvaluator
 */

/**
 * @typedef {function(EvaluationContext):(Array<number>|string)} ColorLikeEvaluator
 */

/**
 * @typedef {function(EvaluationContext):Array<number>} NumberArrayEvaluator
 */

/**
 * @typedef {function(EvaluationContext):Array<number>} CoordinateEvaluator
 */

/**
 * @typedef {function(EvaluationContext):(Array<number>)} SizeEvaluator
 */

/**
 * @typedef {function(EvaluationContext):(Array<number>|number)} SizeLikeEvaluator
 */

/**
 * @param {import('./expression.js').EncodedExpression} encoded The encoded expression.
 * @param {number} type The expected type.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {ExpressionEvaluator} The expression evaluator.
 */
export function buildExpression(encoded, type, context) {
  var expression = parse(encoded, context);
  if (!overlapsType(type, expression.type)) {
    var expected = typeName(type);
    var actual = typeName(expression.type);
    throw new Error("Expected expression to be of type ".concat(expected, ", got ").concat(actual));
  }
  return compileExpression(expression, context);
}

/**
 * @param {import("./expression.js").Expression} expression The expression.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {ExpressionEvaluator} The evaluator function.
 */
function compileExpression(expression, context) {
  if (expression instanceof LiteralExpression) {
    // convert colors to array if possible
    if (expression.type === ColorType && typeof expression.value === 'string') {
      var colorValue = fromString(expression.value);
      return function () {
        return colorValue;
      };
    }
    return function () {
      return expression.value;
    };
  }
  var operator = expression.operator;
  switch (operator) {
    case Ops.Number:
    case Ops.String:
    case Ops.Coalesce:
      {
        return compileAssertionExpression(expression, context);
      }
    case Ops.Get:
    case Ops.Var:
      {
        return compileAccessorExpression(expression, context);
      }
    case Ops.Id:
      {
        return function (context) {
          return context.featureId;
        };
      }
    case Ops.GeometryType:
      {
        return function (context) {
          return context.geometryType;
        };
      }
    case Ops.Concat:
      {
        var args = expression.args.map(function (e) {
          return compileExpression(e, context);
        });
        return function (context) {
          var _ref;
          return (_ref = '').concat.apply(_ref, _toConsumableArray(args.map(function (arg) {
            return arg(context).toString();
          })));
        };
      }
    case Ops.Resolution:
      {
        return function (context) {
          return context.resolution;
        };
      }
    case Ops.Any:
    case Ops.All:
    case Ops.Between:
    case Ops.In:
    case Ops.Not:
      {
        return compileLogicalExpression(expression, context);
      }
    case Ops.Equal:
    case Ops.NotEqual:
    case Ops.LessThan:
    case Ops.LessThanOrEqualTo:
    case Ops.GreaterThan:
    case Ops.GreaterThanOrEqualTo:
      {
        return compileComparisonExpression(expression, context);
      }
    case Ops.Multiply:
    case Ops.Divide:
    case Ops.Add:
    case Ops.Subtract:
    case Ops.Clamp:
    case Ops.Mod:
    case Ops.Pow:
    case Ops.Abs:
    case Ops.Floor:
    case Ops.Ceil:
    case Ops.Round:
    case Ops.Sin:
    case Ops.Cos:
    case Ops.Atan:
    case Ops.Sqrt:
      {
        return compileNumericExpression(expression, context);
      }
    case Ops.Case:
      {
        return compileCaseExpression(expression, context);
      }
    case Ops.Match:
      {
        return compileMatchExpression(expression, context);
      }
    case Ops.Interpolate:
      {
        return compileInterpolateExpression(expression, context);
      }
    case Ops.ToString:
      {
        return compileConvertExpression(expression, context);
      }
    default:
      {
        throw new Error("Unsupported operator ".concat(operator));
      }
    // TODO: unimplemented
    // Ops.Zoom
    // Ops.Time
    // Ops.Array
    // Ops.Color
    // Ops.Band
    // Ops.Palette
  }
}

/**
 * @param {import('./expression.js').CallExpression} expression The call expression.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {ExpressionEvaluator} The evaluator function.
 */
function compileAssertionExpression(expression, context) {
  var type = expression.operator;
  var length = expression.args.length;
  var args = new Array(length);
  for (var i = 0; i < length; ++i) {
    args[i] = compileExpression(expression.args[i], context);
  }
  switch (type) {
    case Ops.Coalesce:
      {
        return function (context) {
          for (var _i = 0; _i < length; ++_i) {
            var value = args[_i](context);
            if (typeof value !== 'undefined' && value !== null) {
              return value;
            }
          }
          throw new Error('Expected one of the values to be non-null');
        };
      }
    case Ops.Number:
    case Ops.String:
      {
        return function (context) {
          for (var _i2 = 0; _i2 < length; ++_i2) {
            var value = args[_i2](context);
            if (_typeof(value) === type) {
              return value;
            }
          }
          throw new Error("Expected one of the values to be a ".concat(type));
        };
      }
    default:
      {
        throw new Error("Unsupported assertion operator ".concat(type));
      }
  }
}

/**
 * @param {import('./expression.js').CallExpression} expression The call expression.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {ExpressionEvaluator} The evaluator function.
 */
function compileAccessorExpression(expression, context) {
  var nameExpression = /** @type {LiteralExpression} */expression.args[0];
  var name = /** @type {string} */nameExpression.value;
  switch (expression.operator) {
    case Ops.Get:
      {
        return function (context) {
          return context.properties[name];
        };
      }
    case Ops.Var:
      {
        return function (context) {
          return context.variables[name];
        };
      }
    default:
      {
        throw new Error("Unsupported accessor operator ".concat(expression.operator));
      }
  }
}

/**
 * @param {import('./expression.js').CallExpression} expression The call expression.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {BooleanEvaluator} The evaluator function.
 */
function compileComparisonExpression(expression, context) {
  var op = expression.operator;
  var left = compileExpression(expression.args[0], context);
  var right = compileExpression(expression.args[1], context);
  switch (op) {
    case Ops.Equal:
      {
        return function (context) {
          return left(context) === right(context);
        };
      }
    case Ops.NotEqual:
      {
        return function (context) {
          return left(context) !== right(context);
        };
      }
    case Ops.LessThan:
      {
        return function (context) {
          return left(context) < right(context);
        };
      }
    case Ops.LessThanOrEqualTo:
      {
        return function (context) {
          return left(context) <= right(context);
        };
      }
    case Ops.GreaterThan:
      {
        return function (context) {
          return left(context) > right(context);
        };
      }
    case Ops.GreaterThanOrEqualTo:
      {
        return function (context) {
          return left(context) >= right(context);
        };
      }
    default:
      {
        throw new Error("Unsupported comparison operator ".concat(op));
      }
  }
}

/**
 * @param {import('./expression.js').CallExpression} expression The call expression.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {BooleanEvaluator} The evaluator function.
 */
function compileLogicalExpression(expression, context) {
  var op = expression.operator;
  var length = expression.args.length;
  var args = new Array(length);
  for (var i = 0; i < length; ++i) {
    args[i] = compileExpression(expression.args[i], context);
  }
  switch (op) {
    case Ops.Any:
      {
        return function (context) {
          for (var _i3 = 0; _i3 < length; ++_i3) {
            if (args[_i3](context)) {
              return true;
            }
          }
          return false;
        };
      }
    case Ops.All:
      {
        return function (context) {
          for (var _i4 = 0; _i4 < length; ++_i4) {
            if (!args[_i4](context)) {
              return false;
            }
          }
          return true;
        };
      }
    case Ops.Between:
      {
        return function (context) {
          var value = args[0](context);
          var min = args[1](context);
          var max = args[2](context);
          return value >= min && value <= max;
        };
      }
    case Ops.In:
      {
        return function (context) {
          var value = args[0](context);
          for (var _i5 = 1; _i5 < length; ++_i5) {
            if (value === args[_i5](context)) {
              return true;
            }
          }
          return false;
        };
      }
    case Ops.Not:
      {
        return function (context) {
          return !args[0](context);
        };
      }
    default:
      {
        throw new Error("Unsupported logical operator ".concat(op));
      }
  }
}

/**
 * @param {import('./expression.js').CallExpression} expression The call expression.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {NumberEvaluator} The evaluator function.
 */
function compileNumericExpression(expression, context) {
  var op = expression.operator;
  var length = expression.args.length;
  var args = new Array(length);
  for (var i = 0; i < length; ++i) {
    args[i] = compileExpression(expression.args[i], context);
  }
  switch (op) {
    case Ops.Multiply:
      {
        return function (context) {
          var value = 1;
          for (var _i6 = 0; _i6 < length; ++_i6) {
            value *= args[_i6](context);
          }
          return value;
        };
      }
    case Ops.Divide:
      {
        return function (context) {
          return args[0](context) / args[1](context);
        };
      }
    case Ops.Add:
      {
        return function (context) {
          var value = 0;
          for (var _i7 = 0; _i7 < length; ++_i7) {
            value += args[_i7](context);
          }
          return value;
        };
      }
    case Ops.Subtract:
      {
        return function (context) {
          return args[0](context) - args[1](context);
        };
      }
    case Ops.Clamp:
      {
        return function (context) {
          var value = args[0](context);
          var min = args[1](context);
          if (value < min) {
            return min;
          }
          var max = args[2](context);
          if (value > max) {
            return max;
          }
          return value;
        };
      }
    case Ops.Mod:
      {
        return function (context) {
          return args[0](context) % args[1](context);
        };
      }
    case Ops.Pow:
      {
        return function (context) {
          return Math.pow(args[0](context), args[1](context));
        };
      }
    case Ops.Abs:
      {
        return function (context) {
          return Math.abs(args[0](context));
        };
      }
    case Ops.Floor:
      {
        return function (context) {
          return Math.floor(args[0](context));
        };
      }
    case Ops.Ceil:
      {
        return function (context) {
          return Math.ceil(args[0](context));
        };
      }
    case Ops.Round:
      {
        return function (context) {
          return Math.round(args[0](context));
        };
      }
    case Ops.Sin:
      {
        return function (context) {
          return Math.sin(args[0](context));
        };
      }
    case Ops.Cos:
      {
        return function (context) {
          return Math.cos(args[0](context));
        };
      }
    case Ops.Atan:
      {
        if (length === 2) {
          return function (context) {
            return Math.atan2(args[0](context), args[1](context));
          };
        }
        return function (context) {
          return Math.atan(args[0](context));
        };
      }
    case Ops.Sqrt:
      {
        return function (context) {
          return Math.sqrt(args[0](context));
        };
      }
    default:
      {
        throw new Error("Unsupported numeric operator ".concat(op));
      }
  }
}

/**
 * @param {import('./expression.js').CallExpression} expression The call expression.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {ExpressionEvaluator} The evaluator function.
 */
function compileCaseExpression(expression, context) {
  var length = expression.args.length;
  var args = new Array(length);
  for (var i = 0; i < length; ++i) {
    args[i] = compileExpression(expression.args[i], context);
  }
  return function (context) {
    for (var _i8 = 0; _i8 < length - 1; _i8 += 2) {
      var condition = args[_i8](context);
      if (condition) {
        return args[_i8 + 1](context);
      }
    }
    return args[length - 1](context);
  };
}

/**
 * @param {import('./expression.js').CallExpression} expression The call expression.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {ExpressionEvaluator} The evaluator function.
 */
function compileMatchExpression(expression, context) {
  var length = expression.args.length;
  var args = new Array(length);
  for (var i = 0; i < length; ++i) {
    args[i] = compileExpression(expression.args[i], context);
  }
  return function (context) {
    var value = args[0](context);
    for (var _i9 = 1; _i9 < length; _i9 += 2) {
      if (value === args[_i9](context)) {
        return args[_i9 + 1](context);
      }
    }
    return args[length - 1](context);
  };
}

/**
 * @param {import('./expression.js').CallExpression} expression The call expression.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {ExpressionEvaluator} The evaluator function.
 */
function compileInterpolateExpression(expression, context) {
  var length = expression.args.length;
  var args = new Array(length);
  for (var i = 0; i < length; ++i) {
    args[i] = compileExpression(expression.args[i], context);
  }
  return function (context) {
    var base = args[0](context);
    var value = args[1](context);
    var previousInput;
    var previousOutput;
    for (var _i10 = 2; _i10 < length; _i10 += 2) {
      var input = args[_i10](context);
      var output = args[_i10 + 1](context);
      var isColor = Array.isArray(output);
      if (isColor) {
        output = withAlpha(output);
      }
      if (input >= value) {
        if (_i10 === 2) {
          return output;
        }
        if (isColor) {
          return interpolateColor(base, value, previousInput, previousOutput, input, output);
        }
        return interpolateNumber(base, value, previousInput, previousOutput, input, output);
      }
      previousInput = input;
      previousOutput = output;
    }
    return previousOutput;
  };
}

/**
 * @param {import('./expression.js').CallExpression} expression The call expression.
 * @param {import('./expression.js').ParsingContext} context The parsing context.
 * @return {ExpressionEvaluator} The evaluator function.
 */
function compileConvertExpression(expression, context) {
  var op = expression.operator;
  var length = expression.args.length;
  var args = new Array(length);
  for (var i = 0; i < length; ++i) {
    args[i] = compileExpression(expression.args[i], context);
  }
  switch (op) {
    case Ops.ToString:
      {
        return function (context) {
          var value = args[0](context);
          if (expression.args[0].type === ColorType) {
            return toString(value);
          }
          return value.toString();
        };
      }
    default:
      {
        throw new Error("Unsupported convert operator ".concat(op));
      }
  }
}

/**
 * @param {number} base The base.
 * @param {number} value The value.
 * @param {number} input1 The first input value.
 * @param {number} output1 The first output value.
 * @param {number} input2 The second input value.
 * @param {number} output2 The second output value.
 * @return {number} The interpolated value.
 */
function interpolateNumber(base, value, input1, output1, input2, output2) {
  var delta = input2 - input1;
  if (delta === 0) {
    return output1;
  }
  var along = value - input1;
  var factor = base === 1 ? along / delta : (Math.pow(base, along) - 1) / (Math.pow(base, delta) - 1);
  return output1 + factor * (output2 - output1);
}

/**
 * @param {number} base The base.
 * @param {number} value The value.
 * @param {number} input1 The first input value.
 * @param {import('../color.js').Color} rgba1 The first output value.
 * @param {number} input2 The second input value.
 * @param {import('../color.js').Color} rgba2 The second output value.
 * @return {import('../color.js').Color} The interpolated color.
 */
function interpolateColor(base, value, input1, rgba1, input2, rgba2) {
  var delta = input2 - input1;
  if (delta === 0) {
    return rgba1;
  }
  var lcha1 = rgbaToLcha(rgba1);
  var lcha2 = rgbaToLcha(rgba2);
  var deltaHue = lcha2[2] - lcha1[2];
  if (deltaHue > 180) {
    deltaHue -= 360;
  } else if (deltaHue < -180) {
    deltaHue += 360;
  }
  var lcha = [interpolateNumber(base, value, input1, lcha1[0], input2, lcha2[0]), interpolateNumber(base, value, input1, lcha1[1], input2, lcha2[1]), lcha1[2] + interpolateNumber(base, value, input1, 0, input2, deltaHue), interpolateNumber(base, value, input1, rgba1[3], input2, rgba2[3])];
  return normalize(lchaToRgba(lcha));
}